/**
 * Five-field cron ("30 3 * * *") and time-zone math for scheduled-job check-ins. Pure: no clock, no I/O.
 *
 * Fields: minute hour day-of-month month day-of-week, each `*`, numbers, ranges `a-b`, steps `*\/n` or
 * `a-b/n`, and lists `a,b`. Month and weekday names (jan, mon) and the macros @hourly @daily @weekly
 * @monthly @yearly work too. When both day fields are restricted a day matches either (classic cron).
 *
 * Times are matched on the wall clock of a time zone. Across DST: a time the clock skips (02:30 on the
 * spring-forward night) counts as the same moment an hour later, and a time that happens twice (01:30 on
 * the fall-back night) counts once, the first time.
 */

const MIN = 60_000;
const DAY = 86_400_000;

export type Cron = {
  minutes: ReadonlySet<number>;
  hours: ReadonlySet<number>;
  days: ReadonlySet<number>;
  months: ReadonlySet<number>;
  weekdays: ReadonlySet<number>;
  /** Day-of-month or day-of-week was `*`: then a day must match both (the star matches all). */
  anyDay: boolean;
};

const MACROS: Record<string, string> = {
  "@yearly": "0 0 1 1 *",
  "@annually": "0 0 1 1 *",
  "@monthly": "0 0 1 * *",
  "@weekly": "0 0 * * 0",
  "@daily": "0 0 * * *",
  "@midnight": "0 0 * * *",
  "@hourly": "0 * * * *",
};

const MONTH_NAMES = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const DAY_NAMES = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

type FieldSpec = { min: number; max: number; names?: string[]; namesFrom?: number };

const FIELDS: readonly FieldSpec[] = [
  { min: 0, max: 59 },
  { min: 0, max: 23 },
  { min: 1, max: 31 },
  { min: 1, max: 12, names: MONTH_NAMES, namesFrom: 1 },
  // 7 is Sunday too.
  { min: 0, max: 7, names: DAY_NAMES, namesFrom: 0 },
];

export class CronError extends Error {
  constructor(detail: string) {
    super(detail);
    this.name = "CronError";
  }
}

function value(raw: string, f: FieldSpec): number {
  const lower = raw.toLowerCase();
  const named = f.names?.indexOf(lower) ?? -1;
  if (named >= 0) return named + (f.namesFrom ?? 0);
  if (!/^\d{1,2}$/.test(raw)) throw new CronError(`"${raw.slice(0, 20)}" is not a number`);
  const n = Number(raw);
  if (n < f.min || n > f.max) throw new CronError(`${n} is outside ${f.min}-${f.max}`);
  return n;
}

function field(raw: string, f: FieldSpec): Set<number> {
  const out = new Set<number>();
  for (const part of raw.split(",")) {
    const [range = "", stepRaw] = part.split("/");
    if (part.split("/").length > 2) throw new CronError(`"${part.slice(0, 20)}" has more than one step`);
    let step = 1;
    if (stepRaw !== undefined) {
      if (!/^\d{1,2}$/.test(stepRaw) || Number(stepRaw) < 1) throw new CronError(`bad step "${stepRaw.slice(0, 20)}"`);
      step = Number(stepRaw);
    }
    let lo: number;
    let hi: number;
    if (range === "*") {
      lo = f.min;
      hi = f.max;
    } else if (range.includes("-")) {
      const [a = "", b = ""] = range.split("-");
      lo = value(a, f);
      hi = value(b, f);
      if (lo > hi) throw new CronError(`range ${range.slice(0, 20)} runs backwards`);
    } else {
      lo = value(range, f);
      // "5/15" means from 5 to the end, every 15.
      hi = stepRaw !== undefined ? f.max : lo;
    }
    for (let n = lo; n <= hi; n += step) out.add(n);
  }
  return out;
}

/** Parses a cron expression. Throws CronError with a short reason. */
export function parseCron(expr: string): Cron {
  const text = expr.trim();
  const spec = MACROS[text.toLowerCase()] ?? text;
  const parts = spec.split(/\s+/);
  if (parts.length !== 5) throw new CronError("use 5 fields: minute hour day month weekday");
  const [mi, ho, dm, mo, dw] = parts.map((p, i) => field(p, FIELDS[i] as FieldSpec)) as [
    Set<number>,
    Set<number>,
    Set<number>,
    Set<number>,
    Set<number>,
  ];
  if (dw.has(7)) dw.add(0);
  return {
    minutes: mi,
    hours: ho,
    days: dm,
    months: mo,
    weekdays: dw,
    anyDay: parts[2] === "*" || parts[4] === "*",
  };
}

/** True when `expr` parses and fires at least once (so "0 0 31 2 *" is rejected). */
export function isValidCron(expr: string): boolean {
  try {
    return nextCronTick(parseCron(expr), 0, "UTC") !== null;
  } catch {
    return false;
  }
}

// ───────────────────────────── time zones ─────────────────────────────

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });
    formatters.set(tz, f);
  }
  return f;
}

/**
 * The wall clock in `tz` at `atMs`, as a number: epoch ms of that clock reading taken as if it were UTC.
 * Seconds kept, milliseconds dropped.
 */
export function wallClock(atMs: number, tz: string): number {
  const p: Record<string, number> = {};
  for (const part of formatter(tz).formatToParts(new Date(atMs))) {
    if (part.type !== "literal") p[part.type] = Number(part.value);
  }
  return Date.UTC(p.year ?? 1970, (p.month ?? 1) - 1, p.day ?? 1, p.hour ?? 0, p.minute ?? 0, p.second ?? 0);
}

/** `tz`'s offset from UTC at `atMs`, in ms (positive east of Greenwich). */
export function zoneOffset(atMs: number, tz: string): number {
  return wallClock(atMs, tz) - (atMs - (((atMs % 1000) + 1000) % 1000));
}

/**
 * The moment a wall-clock reading happens in `tz` (`wall` as from wallClock). A reading that happens
 * twice gives the first; one the clock skips gives the moment it would have been on the old offset
 * (02:30 on a spring-forward night is 03:30 new time).
 */
export function fromWallClock(wall: number, tz: string): number {
  const before = zoneOffset(wall - DAY, tz);
  const after = zoneOffset(wall + DAY, tz);
  const fits = [wall - before, wall - after].filter((t) => wallClock(t, tz) === wall);
  return fits.length > 0 ? Math.min(...fits) : wall - before;
}

/**
 * The first time after `afterMs` that `cron` fires on `tz`'s wall clock, or null when it never fires
 * in the next five years.
 */
export function nextCronTick(cron: Cron, afterMs: number, tz: string): number | null {
  const startWall = wallClock(afterMs, tz);
  let w = Math.floor(startWall / MIN) * MIN + MIN;
  const limit = startWall + 5 * 366 * DAY;
  // Each step jumps to the next month, day, hour or minute that could match: a few thousand at most.
  while (w <= limit) {
    const d = new Date(w);
    const y = d.getUTCFullYear();
    const mo = d.getUTCMonth();
    const day = d.getUTCDate();
    const h = d.getUTCHours();
    if (!cron.months.has(mo + 1)) {
      w = Date.UTC(y, mo + 1, 1);
      continue;
    }
    const domOk = cron.days.has(day);
    const dowOk = cron.weekdays.has(d.getUTCDay());
    if (cron.anyDay ? !(domOk && dowOk) : !(domOk || dowOk)) {
      w = Date.UTC(y, mo, day + 1);
      continue;
    }
    if (!cron.hours.has(h)) {
      w = Date.UTC(y, mo, day, h + 1);
      continue;
    }
    if (!cron.minutes.has(d.getUTCMinutes())) {
      w += MIN;
      continue;
    }
    const t = fromWallClock(w, tz);
    if (t > afterMs) return t;
    w += MIN;
  }
  return null;
}

/** Seconds between the next two ticks after `afterMs` (the job's usual gap), or null if it never fires. */
export function cronGapSeconds(cron: Cron, afterMs: number, tz: string): number | null {
  const a = nextCronTick(cron, afterMs, tz);
  if (a === null) return null;
  const b = nextCronTick(cron, a, tz);
  return b === null ? null : Math.round((b - a) / 1000);
}
